import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  type AccountInfo,
  type Configuration,
} from "@azure/msal-browser";

/**
 * Entra SSO via MSAL. Client + tenant come from build-time env
 * (VITE_ENTRA_CLIENT_ID / VITE_ENTRA_TENANT_ID). If unset, `authEnabled` is
 * false and the app runs open against the (also-open) API.
 *
 * Flow: silent SSO off the user's existing Microsoft browser session first
 * (no prompt). Only when there's no active session do we send them to the
 * standard Microsoft login page.
 */
const clientId = import.meta.env["VITE_ENTRA_CLIENT_ID"] as string | undefined;
const tenantId = import.meta.env["VITE_ENTRA_TENANT_ID"] as string | undefined;

export const authEnabled = Boolean(clientId && tenantId);

/** API scope the SPA requests; the resulting access token is what the Lambda validates. */
export const apiScopes = [
  (import.meta.env["VITE_API_SCOPE"] as string | undefined) ?? "openid",
];

const config: Configuration = {
  auth: {
    clientId: clientId ?? "unconfigured",
    authority: `https://login.microsoftonline.com/${tenantId ?? "common"}`,
    redirectUri: window.location.origin,
  },
  cache: { cacheLocation: "sessionStorage" },
};

export const msal = new PublicClientApplication(config);

/**
 * Initialize MSAL, process any redirect response, and establish the active
 * account — via cache, then silent SSO (existing MS session), then a full-page
 * redirect to the Microsoft login. Call once before rendering.
 */
export async function initAuth(): Promise<void> {
  if (!authEnabled) return;
  await msal.initialize();

  const redirect = await msal.handleRedirectPromise();
  if (redirect?.account) {
    msal.setActiveAccount(redirect.account);
    return;
  }

  const cached = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
  if (cached) {
    msal.setActiveAccount(cached);
    return;
  }

  // No cached account — try silent SSO off the browser's Microsoft session.
  try {
    const res = await msal.ssoSilent({ scopes: apiScopes });
    msal.setActiveAccount(res.account);
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      // Not signed in to Microsoft — send them to the standard login page.
      await msal.loginRedirect({ scopes: apiScopes });
      return; // redirect in progress
    }
    throw e;
  }
}

/** Return an access token for the API, or null when auth is disabled. */
export async function getToken(): Promise<string | null> {
  if (!authEnabled) return null;
  const account = msal.getActiveAccount();
  if (!account) {
    // initAuth couldn't establish a session; kick off interactive login.
    await msal.loginRedirect({ scopes: apiScopes });
    return null;
  }
  try {
    const res = await msal.acquireTokenSilent({ account, scopes: apiScopes });
    return res.accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      await msal.acquireTokenRedirect({ account, scopes: apiScopes });
      return null;
    }
    throw e;
  }
}

function activeAccount(): AccountInfo | null {
  return msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? null;
}

export function currentUserInitials(): string {
  const name = activeAccount()?.name ?? "";
  const parts = name.split(" ").filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}
